import { useState, type FormEvent } from 'react'
import { cleanVatRate, formatEur, parsePriceCents, parseQty, taxLabel, type OrderItemDTO, type ServiceDTO } from '@wf/shared'
import { useServicesList } from '../prices/useServices'
import { LineEditForm } from './LineEditForm'
import { VatSelect } from './VatSelect'
import { useAddItem, useDeleteItem, usePatchItem } from './useOrders'

/** Arbeits-/Leistungs-Positionen eines Auftrags (nur mit Rechnungs-Recht sichtbar). Anlegen mit Netto-Preis +
 *  MwSt + Menge; beim Tippen werden Leistungen aus „Preise" vorgeschlagen (Übernahme füllt Preis + Satz).
 *  Geld ausschließlich über @wf/shared. */
export function OrderItems({ orderId, items, canManage }: { orderId: number; items: OrderItemDTO[]; canManage: boolean }) {
  const add = useAddItem(orderId)
  const patch = usePatchItem(orderId)
  const remove = useDeleteItem(orderId)
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [vatRate, setVatRate] = useState(19)
  const [qty, setQty] = useState('1')
  const [editingId, setEditingId] = useState<number | null>(null)
  const services = useServicesList()

  // Vorschläge aus dem Preise-Katalog ab 2 Zeichen (Bezeichnung), wenn noch keine fertige Übernahme erfolgte.
  const query = name.trim().toLowerCase()
  const matches =
    query.length >= 2 ? (services.data?.services ?? []).filter((s) => s.name.toLowerCase().includes(query)).slice(0, 6) : []

  function resetAdd() {
    setName('')
    setPrice('')
    setVatRate(19)
    setQty('1')
  }

  function submitAdd(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    add.mutate(
      { name: name.trim(), priceCents: parsePriceCents(price), vatRate: cleanVatRate(vatRate), qty: parseQty(qty) },
      { onSuccess: resetAdd },
    )
  }

  // Katalog-Leistung übernehmen = direkt als Position speichern (kein zweiter Klick auf „Arbeit" nötig;
  // konsistent zur Auftrags-Anlage, wo „Übernehmen" die Leistung ebenfalls vormerkt/mitschreibt). Menge 1,
  // serviceId für die Katalog-Verknüpfung. Nach Erfolg die Eingabe/Suche zurücksetzen → Vorschlagsliste schließt.
  function pickService(service: ServiceDTO) {
    add.mutate(
      { name: service.name, priceCents: service.priceCents, vatRate: cleanVatRate(service.vatRate), qty: 1, serviceId: service.id },
      { onSuccess: resetAdd },
    )
  }

  return (
    <section className="wf-lines">
      <span className="wf-label wf-sectiontitle">Arbeiten / Leistungen</span>
      <div className="wf-linelist">
        {items.map((item) =>
          editingId === item.id ? (
            <LineEditForm
              key={item.id}
              label="Position"
              busy={patch.isPending}
              initial={{ name: item.name, priceCents: item.priceCents, vatRate: item.vatRate, qty: item.qty }}
              onSave={(draft) => patch.mutate({ itemId: item.id, body: draft }, { onSuccess: () => setEditingId(null) })}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div key={item.id} className="wf-line">
              <div className="wf-line__main">
                <div className="wf-line__name">{item.name}</div>
                <div className="wf-line__sub">
                  {item.qty}× {formatEur(item.priceCents)} · {taxLabel(item.vatRate)} ={' '}
                  <span className="wf-tnum">{formatEur(item.priceCents * item.qty)}</span>
                </div>
              </div>
              {canManage ? (
                <button type="button" className="wf-iconbtn" aria-label={`Position bearbeiten: ${item.name}`} onClick={() => setEditingId(item.id)}>
                  ✎
                </button>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  className="wf-iconbtn"
                  aria-label={`Position entfernen: ${item.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(item.id)}
                >
                  ✕
                </button>
              ) : null}
            </div>
          ),
        )}
        {items.length === 0 ? <div className="wf-line__sub">Noch keine Arbeits-Positionen</div> : null}
      </div>
      {canManage ? (
        <div>
          <form className="wf-line-add" onSubmit={submitAdd}>
            <input className="wf-input" placeholder="Arbeit / Leistung" aria-label="Positions-Bezeichnung" value={name} onChange={(e) => setName(e.target.value)} />
            <input className="wf-input wf-tnum" placeholder="€ netto" inputMode="decimal" aria-label="Positions-Nettopreis" value={price} onChange={(e) => setPrice(e.target.value)} />
            <VatSelect value={vatRate} onChange={(r) => setVatRate(cleanVatRate(r))} label="Positions-MwSt" />
            <input className="wf-input wf-tnum wf-line__qty" inputMode="numeric" aria-label="Positions-Menge" value={qty} onChange={(e) => setQty(e.target.value)} />
            <button type="submit" className="wf-btn wf-btn--ghost" disabled={add.isPending || !name.trim()}>
              Arbeit
            </button>
          </form>
          {matches.length > 0 ? (
            <div className="wf-linelist">
              <span className="wf-line__sub">Aus „Preise" übernehmen:</span>
              {matches.map((service) => (
                <div key={service.id} className="wf-line">
                  <span className="wf-line__main">
                    <span className="wf-line__name">{service.name}</span>
                    <span className="wf-line__sub wf-tnum">
                      {formatEur(service.priceCents)} · {taxLabel(service.vatRate)}
                    </span>
                  </span>
                  <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" disabled={add.isPending} onClick={() => pickService(service)}>
                    Übernehmen
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
